<script setup>
import { computed } from 'vue'

const props = defineProps({
    content: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                type: rawValue => rawValue?.type === undefined || typeof rawValue?.type === 'string',
                ical: rawValue => rawValue?.ical === undefined || typeof rawValue.ical === 'string',
                myceExclusive: rawValue => rawValue?.myceExclusive === undefined || typeof rawValue.myceExclusive === 'boolean',
                shopUid: rawValue => rawValue?.shopUid === undefined || typeof rawValue.shopUid === 'number',
                title: rawValue => rawValue?.details?.title === undefined || typeof rawValue?.details?.title === 'string',
                abstract: rawValue => rawValue?.details?.abstract === undefined || typeof rawValue?.details?.abstract === 'string',
                dateText: rawValue => rawValue?.details?.dateText === undefined || typeof rawValue.details.dateText === 'string',
                timeText: rawValue => rawValue?.details?.timeText === undefined || typeof rawValue.details.timeText === 'string',
                downloadLinkUrl: rawValue => rawValue?.details?.downloadLinkUrl === undefined || typeof rawValue.details.downloadLinkUrl === 'string',
                link: rawValue => rawValue?.details?.link === undefined || typeof rawValue.details.link === 'string',
                imageUrl: rawValue => rawValue?.details?.imageUrl === undefined || typeof rawValue.details.imageUrl === 'string',
                content: rawValue => rawValue?.details?.content === undefined || typeof rawValue.details.content === 'string'
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch Center Details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

// Fetch Shop Details if `shopUid` is provided
const shopUid = props.content.shopUid
const { data: shopDetails, suspense: shopDetailSuspense } = shopUid ? useFetchShopDetail(shopUid) : { data: null, suspense: async () => {} }

// Prefetch Data on Server
onServerPrefetch(async () => {
    await centerDetailSuspense()
    if (shopUid) await shopDetailSuspense()
})

// Compute configs
const metaConfig = computed(() => {
    return {
        type: props?.content?.type,
        dateText: props?.content?.details?.dateText,
        timeText: props?.content?.details?.timeText,
        myceExclusive: props?.content?.myceExclusive,
        ical: props?.content?.ical,
        location: props?.content?.shopUid ? shopDetails?.value?.name : centerDetails.value?.centerName
    }
})

const articleConfig = computed(() => {
    return {
        title: props?.content?.details?.title,
        abstract: props?.content?.details?.abstract,
        content: props?.content?.details?.content
    }
})

const imageTypeConfig = computed(() => {
    return {
        imageUrl: props?.content?.details?.imageUrl,
        altText: props?.content?.details?.title
    }
})

const downloadConfig = computed(() => {
    return {
        downloadLinkUrl: props?.content?.details?.downloadLinkUrl,
        downloadLinkText: props?.content?.details?.downloadLinkText,
        downloadFileUrl: props?.content?.details?.downloadFileUrl,
        downloadFileText: props?.content?.details?.downloadFileText
    }
})

const shopSummaryConfig = computed(() => {
    return {
        name: shopDetails.value?.name,
        contacts: shopDetails.value?.contacts,
        hours: shopDetails.value?.hours,
        allowTracking: false
    }
})
</script>

<template>
    <ContentPageHeader :logo-type="shopDetails?.logotype" />
    <ContentPageMeta :meta="metaConfig" />
    <ContentPageArticle :article="articleConfig" />
    <ContentPageImage v-if="content?.details?.imageUrl" :image-type="imageTypeConfig" />
    <ContentPageDownload v-if="content?.details?.downloadLinkUrl || content?.details?.downloadFileUrl" :download="downloadConfig" />
    <ShopSummary v-if="content?.shopUid" :shop-summary="shopSummaryConfig" />
    <ShopCenterPlan v-if="shopDetails?.location?.centerPlan?.link" :center-plan="shopDetails?.location?.centerPlan" />

    <DevOnly>
        <p>shopDetails</p>
        <pre>{{ shopDetails }}</pre>

        <p>content</p>
        <pre>{{ content }}</pre>
    </DevOnly>
</template>
